import React, { useState, useEffect, useRef, useMemo } from 'react';
import './RawData.css';
import PortalHeader from '../../../client-dashboard/clientNavbar/PortalHeader';

// Icons
import {
    IoFolderOpen,
    IoDocumentText,
    IoBarChart,
    IoDownload,
} from 'react-icons/io5';
import { AiOutlineAim } from "react-icons/ai";

import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    LineElement,
    CategoryScale,
    LinearScale,
    PointElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { TbZoomReset, TbZoomIn, TbZoomOut } from 'react-icons/tb';
import { ChartOptions } from 'chart.js';

// Toast imports
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

ChartJS.register(
    LineElement,
    CategoryScale,
    LinearScale,
    PointElement,
    Title,
    Tooltip,
    Legend,
    zoomPlugin
);

interface FileEntry {
    is_dir: boolean;
    name: string;
    path: string;
    children?: FileEntry[];
}

interface FileInfo {
    date: string;
    receiver: string;
    location: { latitude: number; longitude: number };
}

const RawData = () => {
    const [directoryTree, setDirectoryTree] = useState<FileEntry[]>([]);
    const [selectedFile, setSelectedFile] = useState<FileEntry | null>(null);
    const [loadingDirs, setLoadingDirs] = useState<Set<string>>(new Set());
    const [expandedDirs, setExpandedDirs] = useState<Set<string>>(new Set());
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);

    // Plot related state
    const [plotData, setPlotData] = useState<{ x: number[]; y: number[] } | null>(null);
    const [fileInfo, setFileInfo] = useState<FileInfo | null>(null);
    const [isPlotVisible, setIsPlotVisible] = useState(false);
    const chartRef = useRef<any>(null);

    // SCOPE related state
    const [scopeResult, setScopeResult] = useState<string>('');
    const [isScoping, setIsScoping] = useState(false);

    const DETECTOR_IP = '100.82.139.13';

    // To change the max variables in x-axis
    const [isNarrowScreen, setIsNarrowScreen] = useState(window.innerWidth < 1100);
    useEffect(() => {
        const handleResize = () => setIsNarrowScreen(window.innerWidth < 1100);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // To show or not the hint text
    const [isWideScreen, setIsWideScreen] = useState(window.innerWidth > 600);
    useEffect(() => {
        const handleResize = () => setIsWideScreen(window.innerWidth > 600);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const truncateFileName = (name: string, maxLength = 20) => {
        if (name.length <= maxLength) return name;
        const extIndex = name.lastIndexOf('.');
        const ext = extIndex !== -1 ? name.slice(extIndex) : '';
        const base = extIndex !== -1 ? name.slice(0, extIndex) : name;
        return base.slice(0, maxLength - ext.length - 3) + '...' + ext;
    };

    useEffect(() => {
        fetchDirectory('');
    }, []);

    function displayNotification(successOrFail: boolean, message: string) {
        if (successOrFail) {
            toast.success(message, {
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                style: {
                    backgroundColor: '#e7ecf1ff',
                    color: 'black',
                    fontSize: '16px',
                }
            });
        } else {
            toast.error(message, {
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                style: {
                    backgroundColor: '#e7ecf1ff',
                    color: 'black',
                    fontSize: '16px',
                }
            });
        }
    }

    const handleScopeDetector = async () => {
        setIsScoping(true);
        setScopeResult('');

        try {
            const url = new URL(`/apidet/fnnbackend/fnnToolView/`, window.location.origin);
            url.searchParams.append("ip", DETECTOR_IP);
            url.searchParams.append("command", "SCOPE");
            url.searchParams.append("origin", 'dataUSA');

            const response = await fetch(url.toString(), {
                method: "GET",
                headers: { "Content-Type": "application/json" },
                credentials: "same-origin",
            });

            if (!response.ok) throw new Error("Failed to fetch SCOPE data");

            const data = await response.json();
            const formattedResult = data.result.includes(":")
                ? data.result.split(":")[1].trim()
                : data.result;

            setScopeResult(formattedResult || "No data received");
            displayNotification(true, "SCOPE data retrieved successfully");

        } catch (error) {
            console.error("Error fetching SCOPE command:", error);
            displayNotification(false, "There was an issue completing the SCOPE request.");
            setScopeResult("Error retrieving SCOPE data");
        } finally {
            setIsScoping(false);
        }
    };

    // ---------- Directory fetching logic ----------
    const fetchDirectory = async (path: string) => {
        setLoadingDirs((prev) => new Set(prev).add(path));
        try {
            const response = await fetch(
                `/portal/api/croatia/list?path=${encodeURIComponent(path)}`,
                {
                    method: 'GET',
                    credentials: 'same-origin',
                }
            );
            if (response.ok) {
                const data = await response.json();
                const entries: FileEntry[] = data.entries.map((entry: any) => ({
                    ...entry,
                    path: path ? `${path}/${entry.name}` : entry.name,
                    children: entry.is_dir ? [] : undefined,
                }));
                if (!path) setDirectoryTree(entries);
                else
                    setDirectoryTree((prev) =>
                        updateTreeWithChildren(prev, path, entries)
                    );
            } else console.error('Failed to fetch directory');
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingDirs((prev) => {
                const next = new Set(prev);
                next.delete(path);
                return next;
            });
        }
    };

    const updateTreeWithChildren = (
        tree: FileEntry[],
        targetPath: string,
        children: FileEntry[]
    ): FileEntry[] => {
        return tree.map((node) => {
            if (node.is_dir && node.path === targetPath) return { ...node, children };
            else if (node.children)
                return {
                    ...node,
                    children: updateTreeWithChildren(node.children, targetPath, children),
                };
            return node;
        });
    };

    const handleFileClick = (file: FileEntry) => {
        if (!file.is_dir) {
            setSelectedFile(file);
            setPlotData(null);
            setFileInfo(null);
            setIsPlotVisible(false);
        } else {
            setExpandedDirs((prev) => {
                const next = new Set(prev);
                if (next.has(file.path)) next.delete(file.path);
                else {
                    next.add(file.path);
                    if (file.children && file.children.length === 0)
                        fetchDirectory(file.path);
                }
                return next;
            });
        }
    };

    const handleDownload = (file: FileEntry) => {
        const downloadUrl = `/portal/api/croatia/download?path=${encodeURIComponent(
            file.path
        )}`;
        window.open(downloadUrl, '_blank');
    };

    // ---------- Plot logic ----------
    const chartOptions = useMemo<ChartOptions<'line'>>(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            scales: {
                x: {
                    title: { display: true, text: 'Time (s)' },
                    ticks: {
                        autoSkip: true,
                        maxTicksLimit: isNarrowScreen ? 5 : 10,
                        maxRotation: 0,
                        minRotation: 0,
                        callback: function (value: any, index: number, ticks: any) {
                            const labels = this.chart.data.labels;
                            if (labels && labels[index] !== undefined) {
                                const num = Number(labels[index]);

                                // Calculate the range of visible data
                                const visibleLabels = labels.slice(
                                    Math.max(0, index - 5),
                                    Math.min(labels.length, index + 6)
                                ).map(l => Number(l));

                                const min = Math.min(...visibleLabels);
                                const max = Math.max(...visibleLabels);
                                const range = max - min;

                                // Adjust precision based on range
                                if (range < 0.001) {
                                    return num.toFixed(6);
                                } else if (range < 0.01) {
                                    return num.toFixed(5);
                                } else if (range < 0.1) {
                                    return num.toFixed(4);
                                } else if (range < 1) {
                                    return num.toFixed(3);
                                } else {
                                    return num.toExponential(2);
                                }
                            }
                            return value;
                        }
                    },
                },
                y: {
                    title: { display: true, text: 'ADC' },
                },
            },
            plugins: {
                zoom: {
                    pan: { enabled: true, mode: 'x', modifierKey: 'ctrl' },
                    zoom: {
                        wheel: { enabled: false },
                        pinch: { enabled: false },
                        drag: {
                            enabled: true,
                            borderColor: 'rgba(40, 170, 225, 0.5)',
                            borderWidth: 1,
                            backgroundColor: 'rgba(40, 170, 225, 0.15)',
                        },
                        mode: 'xy',
                    },
                },
            },
        }),
        [isNarrowScreen]
    );

    const resetZoom = () => chartRef.current?.resetZoom();
    const zoomIn = () => chartRef.current?.zoom(1.2);
    const zoomOut = () => chartRef.current?.zoom(0.8);

    type LatLng = { latitude: number; longitude: number };

    function normalizeLocation(loc: unknown): LatLng {
        const fallback: LatLng = { latitude: 0, longitude: 0 };

        const toNum = (v: unknown) => {
            const n = typeof v === "string" ? Number(v.trim()) : Number(v);
            return Number.isFinite(n) ? n : null;
        };

        if (loc && typeof loc === "object" && !Array.isArray(loc)) {
            const obj = loc as any;

            if ("latitude" in obj && "longitude" in obj) {
                const lat = toNum(obj.latitude);
                const lon = toNum(obj.longitude);
                if (lat != null && lon != null) return { latitude: lat, longitude: lon };
            }

            if ("0" in obj && "1" in obj) {
                const lat = toNum(obj[0]);
                const lon = toNum(obj[1]);
                if (lat != null && lon != null) return { latitude: lat, longitude: lon };
            }

            return fallback;
        }

        if (Array.isArray(loc)) {
            const lat = toNum(loc[0]);
            const lon = toNum(loc[1]);
            if (lat != null && lon != null) return { latitude: lat, longitude: lon };
            return fallback;
        }

        if (typeof loc === "string") {
            const parts = loc.split(/[, ]+/).filter(Boolean);
            const lat = toNum(parts[0]);
            const lon = toNum(parts[1]);
            if (lat != null && lon != null) return { latitude: lat, longitude: lon };
            return fallback;
        }

        return fallback;
    }

    type RawArrayResponse = {
        path: string;
        filename: string;
        info: {
            location?: string;
            MAC?: string;
            receiver?: string;
            date?: string;
            [key: string]: any;
        };
        ts: number[];
        data: number[];
    };

    const fetchAndPlotFile = async (path: string) => {
        try {
            const response = await fetch(
                `/portal/api/rawdata/browse/array?path=${encodeURIComponent(path)}`,
                { credentials: 'same-origin' }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const payload = (await response.json()) as RawArrayResponse;

            setPlotData({
                x: payload.ts,
                y: payload.data,
            });

            const filename = payload.filename || path.split('/').pop() || '';
            const dateString = filename.split('_')[0]?.slice(0, 19);
            const timestamp =
                payload.info?.date ??
                (dateString
                    ? dateString.replace(/-/g, ':').replace('T', ' ')
                    : 'Unknown');

            const location =
                payload.info?.location ?? { latitude: 0, longitude: 0 };

            setFileInfo({
                date: timestamp,
                receiver: payload.info?.MAC ?? payload.info?.receiver ?? 'Unknown',
                location: normalizeLocation(payload.info?.location),
            });
        } catch (err) {
            console.error('Error fetching/plotting file:', err);
        }
    };

    const downloadCsv = (file: FileEntry) => {
        const url = `/portal/api/rawdata/browse/array?path=${encodeURIComponent(
            file.path
        )}&download=1`;
        window.open(url, '_blank');
    };

    const renderTree = (entries: FileEntry[], level = 0) =>
        entries.map((entry) => (
            <div key={`${level}-${entry.name}`} style={{ marginLeft: level * 16 }}>
                <div
                    className={`file-item ${entry.is_dir
                        ? 'directory-item'
                        : entry.name.endsWith('.bin')
                            ? 'bin-file'
                            : ''
                        }`}
                    onClick={() => handleFileClick(entry)}
                >
                    {entry.is_dir ? <IoFolderOpen /> : <IoDocumentText />}
                    <span style={{ width: '100%' }}>{entry.is_dir ? entry.name : entry.name}</span>
                </div>
                {entry.is_dir && loadingDirs.has(entry.path) && (
                    <p style={{ marginLeft: 16, fontStyle: 'italic', color: '#888' }}>
                        Loading...
                    </p>
                )}
                {entry.is_dir &&
                    expandedDirs.has(entry.path) &&
                    entry.children &&
                    entry.children.length > 0 &&
                    renderTree(entry.children, level + 1)}
            </div>
        ));

    return (
        <div className="overall-div-for-suite">
            <ToastContainer />
            <PortalHeader />

            <div className='raw-data-title-div'>
                <h1 className="raw-data-title">Raw Data Management</h1>

                <button
                    className="plot-btn"
                    onClick={handleScopeDetector}
                    disabled={isScoping}
                >
                    <AiOutlineAim /> {isScoping ? 'Scoping...' : 'Scope Detector'}
                </button>
            </div>

            {scopeResult && (
                <div className='raw-scope'>
                    <h3>Scope Result:</h3>
                    <p style={{ whiteSpace: 'pre-wrap' }}>{scopeResult}</p>
                </div>
            )}

            <button
                className="sidebar-toggle"
                onClick={() => setIsSidebarOpen((prev) => !prev)}
            >
                {isSidebarOpen ? 'Hide File Explorer' : 'Show File Explorer'}
            </button>

            <div className="raw-data-section">
                <div
                    className={`raw-data-sidebar ${isSidebarOpen ? 'open' : 'collapsed'}`}
                >
                    <h2>File Explorer</h2>
                    {renderTree(directoryTree)}
                </div>

                <div className="raw-data-content">
                    {selectedFile ? (
                        <>
                            <div className="raw-file-header">
                                <h2>{selectedFile.name}</h2>
                            </div>

                            {(selectedFile.name.endsWith('.bin') ||
                                selectedFile.name.endsWith('.csv')) && (
                                    <>
                                        <div className="file-actions">
                                            <button
                                                className="download-btn"
                                                onClick={() => downloadCsv(selectedFile)}
                                            >
                                                <IoDownload /> Download
                                            </button>

                                            {selectedFile.name.endsWith('.bin') && (
                                                <>
                                                    <button
                                                        className="plot-btn"
                                                        onClick={() => {
                                                            setIsPlotVisible(true);
                                                            fetchAndPlotFile(selectedFile.path);
                                                        }}
                                                    >
                                                        <IoBarChart /> Plot File
                                                    </button>

                                                    <button className="download-btn" onClick={zoomIn}>
                                                        <TbZoomIn /> Zoom In
                                                    </button>

                                                    <button className="download-btn" onClick={zoomOut}>
                                                        <TbZoomOut /> Zoom Out
                                                    </button>

                                                    <button className="download-btn" onClick={resetZoom}>
                                                        <TbZoomReset /> Reset Zoom
                                                    </button>
                                                </>
                                            )}
                                        </div>

                                        {selectedFile.name.endsWith('.bin') && isPlotVisible && (
                                            <div className="plot-placeholder">
                                                {plotData ? (
                                                    <div className="plot-chart-wrapper">
                                                        <Line
                                                            ref={chartRef}
                                                            data={{
                                                                labels: plotData.x,
                                                                datasets: [
                                                                    {
                                                                        label: 'ADC vs Time (s)',
                                                                        data: plotData.y,
                                                                        borderColor: '#28aae1',
                                                                        borderWidth: 1,
                                                                        pointRadius: 0,
                                                                    },
                                                                ],
                                                            }}
                                                            options={chartOptions}
                                                        />

                                                        {isWideScreen && (
                                                            <p>
                                                                <strong>Hint:</strong> Make a square with your
                                                                mouse for a more targeted zoom
                                                            </p>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <p>Plot preview will be generated here</p>
                                                )}
                                            </div>
                                        )}

                                        {fileInfo && (
                                            <div className="file-metadata">
                                                <h3>Header Data</h3>

                                                <ul className="raw-list">
                                                    {Object.entries(fileInfo).map(([key, value]) => {
                                                        if (key === 'GPS Valid') return null;

                                                        if (key === 'location' && typeof value === 'object') {
                                                            return (
                                                                <React.Fragment key="location">
                                                                    <li className="raw-list-item">
                                                                        <strong className="plot-key">
                                                                            Latitude:
                                                                        </strong>
                                                                        <div className="plot-card-value">
                                                                            {value.latitude.toFixed(5)}
                                                                        </div>
                                                                    </li>
                                                                    <li className="raw-list-item">
                                                                        <strong className="plot-key">
                                                                            Longitude:
                                                                        </strong>
                                                                        <div className="plot-card-value">
                                                                            {value.longitude.toFixed(5)}
                                                                        </div>
                                                                    </li>
                                                                </React.Fragment>
                                                            );
                                                        }

                                                        return (
                                                            <li key={key} className="raw-list-item">
                                                                <strong className="plot-key">
                                                                    {key.charAt(0).toUpperCase() + key.slice(1)}:
                                                                </strong>
                                                                <div className="plot-card-value">
                                                                    {value?.toString()}
                                                                </div>
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            </div>
                                        )}
                                    </>
                                )}
                        </>
                    ) : (
                        <div className="empty-state">
                            <p>Select a file to view details</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RawData;